import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Boxes,
  Clock,
  Cpu,
  FileCog,
  HardDrive,
  Loader2,
  Network,
  Package,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ModulyAutomatu } from '@/components/ModulyAutomatu';
import { UstawieniaAutomatu } from '@/components/UstawieniaAutomatu';
import { SpsConfigDialog } from '@/components/SpsConfigDialog';
import { Tabs } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { nazwaSystemu } from '@/lib/system-windows';
import { useIslands } from '@/lib/islands';
import { fetchKartaAutomatu } from '@/lib/machines-api';

/**
 * KARTA AUTOMATU — wszystko o jednej maszynie, na wzór karty pracownika.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ TA STRONA POWSTAŁA PO DNIU, W KTÓRYM DIAGNOZA JEDNEJ MASZYNY WYMAGAŁA CZYTANIA LICZNIKA
 *  RUCHU INSTANCJI I WYCIĄGANIA ŹRÓDEŁ Z JEJ DYSKU (testówka PESA, 26.08.2026). Lista automatów
 *  odpowiada na pytanie „czy wszystkie żyją"; ta karta na pytanie „co się dzieje z TĄ".
 *
 *  ⚠️ CZEGO TU CELOWO NIE MA: LIMITÓW. `Rationing` w SPS nie ma kolumny `VMID` — zużycie sumuje
 *  się po osobie i towarze niezależnie od automatu. Sekcja „limity tego automatu" byłaby zdaniem
 *  nieprawdziwym o działaniu SPS-a.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Kafelek liczbowy nad treścią — ten sam kształt, co na karcie pracownika. */
function Kafelek({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" title={hint}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Sekcja({
  tytul,
  ikona: Ikona,
  licznik,
  children,
  akcja,
}: {
  tytul: string;
  ikona?: typeof Cpu;
  licznik?: string;
  children: React.ReactNode;
  akcja?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {Ikona && <Ikona className="size-4 text-muted-foreground" />}
          {tytul}
          {licznik !== undefined && <span className="text-xs font-normal text-muted-foreground">{licznik}</span>}
        </h2>
        {akcja}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Pole({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1.5 last:border-b-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm">{children}</span>
    </div>
  );
}

const czas = (iso: string | null): string => (iso === null ? '—' : new Date(iso).toLocaleString('pl-PL'));

/** Bajty po ludzku — jeden kształt, bo liczba w bajtach nic nikomu nie mówi. */
function bajty(n: number | null): string {
  if (n === null) return '—';
  const jednostki = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < jednostki.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${jednostki[i]}`;
}

/**
 * USŁUGA CZASU WINDOWS PO LUDZKU.
 *
 * ⚠️ „WYŁĄCZONA" TO JEDYNY STAN, W KTÓRYM ROZJAZD JEST TRWAŁY — i dlatego jest tu jedynym
 * ostrzeżeniem. Agent czyta `Start` z rejestru `W32Time` (2 automatyczny, 3 ręczny, 4 wyłączona)
 * oraz kod stanu z `sc query`, bo etykiety są tłumaczone na język systemu. Zmierzone na testówce
 * PESA (26.08.2026): Dynabox miał `Start=0x4` i dryfował w jedną stronę.
 */
function UslugaCzasu({ start, dziala }: { start: string | null; dziala: boolean | null }) {
  const { t } = useI18n();
  if (start === null) return <span className="text-muted-foreground">{t('mcard.nieustalone')}</span>;
  if (start === 'wylaczona') {
    return (
      <Badge variant="destructive" title={t('mcard.w32timeOff')}>
        {t('mcard.svc.disabled')}
      </Badge>
    );
  }
  const opis = dziala === null ? t('mcard.svc.unknown') : dziala ? t('mcard.svc.running') : t('mcard.svc.stopped');
  return (
    <span>
      {start === 'automatyczny' ? t('mcard.svc.auto') : start === 'reczny' ? t('mcard.svc.manual') : start}
      <span className="ml-1 text-xs text-muted-foreground">({opis})</span>
    </span>
  );
}

/** Obecność maszyny — te same stany, co na liście automatów. */
function ObecnoscBadge({ stan }: { stan: string }) {
  const { t } = useI18n();
  if (stan === 'ONLINE') return <Badge variant="success">{t('mcard.p.online')}</Badge>;
  if (stan === 'SILENT') return <Badge variant="warning">{t('mcard.p.silent')}</Badge>;
  if (stan === 'NEVER_CONNECTED') return <Badge variant="warning">{t('mcard.p.never')}</Badge>;
  return <Badge variant="neutral">{t('mcard.p.noAgent')}</Badge>;
}

export function MachineCard() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const { groupId } = useIslands();
  const { isAdmin } = useAuth();
  const [spsConfigOtwarty, setSpsConfigOtwarty] = useState(false);
  /**
   * ⚠️ WYBRANA ZAKŁADKA SIEDZI W ADRESIE, nie w `useState` — żeby „wyślij mi link do modułów tej
   * maszyny" znaczyło to samo po odświeżeniu i po wklejeniu komuś. Stan w pamięci widoku wracałby
   * po każdym odświeżeniu na `Ogólne`, a to jest ekran, do którego wraca się z pytaniem.
   */
  const [parametry, ustawParametry] = useSearchParams();
  const wZadresie = parametry.get('zakladka');
  /** ⚠️ „Ustawienia" tylko dla admina — link z tą zakładką u kogoś innego otwiera „Ogólne". */
  const zakladka: 'ogolne' | 'moduly' | 'ustawienia' =
    wZadresie === 'moduly' ? 'moduly' : wZadresie === 'ustawienia' && isAdmin ? 'ustawienia' : 'ogolne';

  const { data, isPending, error } = useQuery({
    queryKey: ['machines', 'card', id, groupId],
    queryFn: () => fetchKartaAutomatu(id!, groupId!),
    enabled: Boolean(id) && Boolean(groupId),
  });

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  if (isPending) {
    return (
      <p className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading')}
      </p>
    );
  }

  /**
   * ⚠️ ODMOWA MÓWI, CO SIĘ STAŁO, i daje drogę powrotną. Najczęstszy powód to przełączenie wyspy
   * przy otwartej karcie — automat należy do poprzedniej, więc API słusznie go nie zna.
   */
  if (error || !data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('mcard.wrocDoListy')}
        </p>
        <Link to="/automaty" className="inline-flex items-center gap-1 text-sm underline">
          <ArrowLeft className="size-4" /> {t('nav.machines')}
        </Link>
      </div>
    );
  }

  const { automat, wyspa, wezel, synchronizacja, ustawieniaSps, wydania, ruch } = data;
  const g = automat.geometria;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/automaty"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> {t('nav.machines')}
        </Link>
        <h1 className="text-lg font-semibold">
          {automat.name}
          <span className="ml-2 font-mono text-sm text-muted-foreground">VMID {automat.vmid}</span>
        </h1>
        {/**
          * ⚠️ OBECNOŚĆ Z API, NIE Z SUROWEGO `online`.
          *
          * ⚠️⚠️ POPRAWKA PO OBEJRZENIU TEJ KARTY NA WDROŻENIU (26.08.2026): nagłówek mówił
          * „offline, ostatni kontakt —", gdy sekcja niżej mówiła „ostatni kontakt 11:47:54".
          * `online` i `lastSeenAt` to pola AUTOMATU z synchronizacji konfiguracji, a nie stan
          * AGENTA — na maszynie, której SPS ich nie wypełnia, zostają puste na zawsze.
          */}
        <ObecnoscBadge stan={automat.obecnosc ?? (automat.online ? 'ONLINE' : 'NO_AGENT')} />
        <span className="text-xs text-muted-foreground">
          {wyspa.name} · ostatni kontakt {czas(automat.kontaktAgenta ?? automat.lastSeenAt)}
        </span>
      </div>

      {/*
        ⚠️ PASEK ZAKŁADEK STOI POD NAGŁÓWKIEM, A NAGŁÓWEK ZOSTAJE NAD NIM. Nazwa maszyny, `VMID`
        i obecność agenta obowiązują w OBU zakładkach — schowanie ich do jednej znaczyłoby, że
        na drugiej nie wiadomo, o której maszynie mowa.
      */}
      <Tabs
        zakladki={[
          { id: 'ogolne', etykieta: t('mcard.zakladka.ogolne'), ikona: <Cpu className="size-4" /> },
          {
            id: 'moduly',
            etykieta: t('mcard.zakladka.moduly'),
            ikona: <Boxes className="size-4" />,
            licznik: g.bebnowZnanych,
          },
          /**
           * ⚠️ USTAWIENIA AUTOMATU (`SetupData`) — od 23.09.2026, na zamówienie „cały setupdata
           * z możliwością edycji". Tylko admin: to ustawienia działania programu na automacie.
           */
          ...(isAdmin
            ? [{ id: 'ustawienia', etykieta: t('mcard.zakladka.ustawienia'), ikona: <Settings2 className="size-4" /> }]
            : []),
        ]}
        wybrana={zakladka}
        onWybor={(z) => ustawParametry(z === 'ogolne' ? {} : { zakladka: z }, { replace: true })}
      />

      {zakladka === 'moduly' ? (
        <ModulyAutomatu machineId={automat.id} groupId={groupId} nazwaMaszyny={automat.name} />
      ) : zakladka === 'ustawienia' ? (
        <UstawieniaAutomatu groupId={groupId} vmid={automat.vmid} />
      ) : (
      <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kafelek label={t('mcard.k.wydan')} value={String(wydania.razem)} hint={`${t('mcard.ostatnieWydanie')}: ${czas(wydania.ostatnie)}`} />
        <Kafelek label={t('mcard.k.przegrodek')} value={String(g.przegrodek)} hint={t('mcard.przegrodkiHint')} />
        <Kafelek label={t('mcard.k.towarow')} value={String(g.towarow)} />
        <Kafelek
          label={`${t('mcard.s.ruch')} (${ruch?.dni ?? 30} ${t('traffic.days')})`}
          value={ruch === null ? '—' : bajty(ruch.razemDoPortalu + ruch.razemZPortalu)}
          hint={ruch === null ? t('mcard.ruchBrak') : `${ruch.razemZadan} ${t('mcard.f.zadan')}`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Sekcja tytul={t('mcard.s.sprzet')} ikona={Cpu}>
          <Pole label={t('mcard.f.nazwa')}>{automat.name}</Pole>
          <Pole label={t('mcard.f.vmid')}>
            <span className="font-mono">{automat.vmid}</span>
          </Pole>
          <Pole label={t('mcard.f.kod')}>{automat.vmCode ?? '—'}</Pole>
          <Pole label={t('mcard.f.bebny')}>
            {g.bebnow}
            {g.bebnowZnanych !== g.bebnow && (
              <span className="ml-1 text-xs text-muted-foreground">
                {t('mcard.portalZna').replace('{n}', String(g.bebnowZnanych))}
              </span>
            )}
          </Pole>
          <Pole label={t('mcard.f.szuflady')}>{g.szuflad}</Pole>
          <Pole label={t('mcard.f.kolumny')}>{g.kolumn}</Pole>
          <Pole label={t('mcard.f.przegrodki')}>{g.przegrodek}</Pole>
          <Pole label={t('mcard.f.wyspa')}>
            {wyspa.name} <span className="font-mono text-xs text-muted-foreground">{wyspa.code}</span>
          </Pole>
          <Pole label={t('mcard.f.tryb')}>{wyspa.tryb === 'SMART_SYNC' ? 'Smart Sync' : t('mcard.trybZwykly')}</Pole>
        </Sekcja>

        <Sekcja
          tytul={t('mcard.s.wezel')}
          ikona={Network}
          akcja={
            isAdmin && wezel !== null ? (
              <Button size="sm" variant="outline" className="h-7" onClick={() => setSpsConfigOtwarty(true)}>
                <FileCog className="mr-1 size-3" />
                SPSConfig.XML
              </Button>
            ) : undefined
          }
        >
          {wezel === null ? (
            <p className="text-sm text-muted-foreground">
              {t('mcard.brakAgenta')}
            </p>
          ) : (
            <>
              <Pole label={t('mcard.f.wezel')}>
                <span className="font-mono text-xs">{wezel.nodeId}</span>
              </Pole>
              <Pole label={t('mcard.f.wersja')}>
                <span className="font-mono text-xs">{wezel.version ?? '—'}</span>
              </Pole>
              <Pole label={t('mcard.f.rola')}>{wezel.nodeRole ?? '—'}</Pole>
              <Pole label={t('mcard.f.stan')}>
                <Badge variant={wezel.potwierdzony ? 'success' : 'warning'}>
                  {wezel.potwierdzony ? t('mcard.potwierdzony') : t('mcard.oczekuje')}
                </Badge>
              </Pole>
              <Pole label={t('mcard.f.komputer')}>{wezel.hostname ?? '—'}</Pole>
              <Pole label={t('mcard.f.ipMaszyny')}>
                <span className="font-mono text-xs">{wezel.machineIp ?? '—'}</span>
              </Pole>
              <Pole label={t('mcard.f.ipPortalu')}>
                <span className="font-mono text-xs">{wezel.lastSeenIp ?? '—'}</span>
              </Pole>
              <Pole label={t('mcard.f.baza')}>
                {wezel.sps.baza ?? '—'}
                {wyspa.masterDatabase !== null && wezel.sps.baza !== null && wezel.sps.baza !== wyspa.masterDatabase && (
                  <Badge variant="warning" className="ml-2">
                    {t('mcard.innaNizMaster').replace('{baza}', wyspa.masterDatabase)}
                  </Badge>
                )}
              </Pole>
              <Pole label={t('mcard.f.serwer')}>
                <span className="font-mono text-xs">{wezel.sps.serwer ?? '—'}</span>
              </Pole>
              {/**
                * SYSTEM I SILNIK SQL — odpowiedź na pytanie, które przy zgłoszeniu awarii pada
                * pierwsze: „na czym to w ogóle stoi".
                *
                * ⚠️ POKAZUJEMY TYLKO TO, CO WIEMY. Fakty jadą przy zmianie, a agent sprawdza je
                * raz na ~godzinę — świeżo wdrożona maszyna nie ma ich jeszcze w pierwszej minucie
                * i wtedy wiersz ma się NIE POJAWIAĆ, zamiast pokazywać kreskę jak brak systemu.
                */}
              {(wezel.system?.osName ?? wezel.system?.osVersion) != null && (
                <Pole label={t('mcard.f.system')}>
                  {/*
                    ⚠️ NAZWA PRZEZ `nazwaSystemu`, NIE WPROST Z FAKTÓW. `ProductName` w rejestrze
                    Windows 11 nadal mówi „Windows 10 Pro" — zgłoszone z PESA-1 (budowa 26200).
                    Poprawka siedzi w `lib/system-windows.ts` razem z uzasadnieniem.
                  */}
                  {nazwaSystemu(wezel.system?.osName, wezel.system?.osVersion) ?? '—'}
                  {wezel.system?.osVersion != null && (
                    <span className="ml-1 text-xs text-muted-foreground">{wezel.system.osVersion}</span>
                  )}
                </Pole>
              )}
              {wezel.system?.sqlVersion != null && (
                <Pole label={t('mcard.f.sqlWersja')}>
                  <span className="font-mono text-xs">{wezel.system.sqlVersion}</span>
                  {wezel.system.sqlEdition != null && (
                    <span className="ml-1 text-xs text-muted-foreground">{wezel.system.sqlEdition}</span>
                  )}
                </Pole>
              )}
              <Pole label={t('mcard.f.kontakt')}>{czas(wezel.lastSeenAt)}</Pole>
              {wezel.problem !== null && (
                <Pole label={t('mcard.f.problem')}>
                  <span className="text-[var(--warning)]">{wezel.problem}</span>
                </Pole>
              )}
              {wezel.uwagi.length > 0 && (
                <Pole label={t('mcard.f.uwagi')}>
                  <span className="block space-y-0.5 text-left text-xs text-muted-foreground">
                    {wezel.uwagi.map((u) => (
                      <span key={u} className="block">
                        {u}
                      </span>
                    ))}
                  </span>
                </Pole>
              )}
            </>
          )}
        </Sekcja>

        {wezel !== null && (
          <Sekcja tytul={t('mcard.s.dyskZegar')} ikona={HardDrive}>
            {/**
              * PASEK ZAJĘTOŚCI DYSKU — ten sam kształt i te same napisy, co na ekranie kopii.
              *
              * ⚠️ NAPISY WZIĘTE Z `kopie.*`, A NIE NAPISANE OD NOWA. Dwa ekrany mówiące o tym samym
              * dysku innymi słowami znaczą dwa ekrany, które trzeba czytać osobno — a próg 10 %
              * jest ten sam, bo to ten sam agent i ta sama odmowa kopii.
              *
              * ⚠️ WOLUMEN PODPISUJEMY LITERĄ OD AGENTA: wylicza ją ze ścieżki swojego katalogu,
              * bo nie każda maszyna trzyma agenta na `C:`.
              */}
            {wezel.dysk.wolneBajty !== null && wezel.dysk.caleBajty !== null && wezel.dysk.caleBajty > 0 ? (
              <div className="space-y-1 border-b border-border/60 py-1.5">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="text-muted-foreground">
                    {t('kopie.dysk').replace('{wolumen}', wezel.dysk.wolumen ?? '?')}
                  </span>
                  <span className="tabular-nums">
                    {t('mcard.dyskZajete').replace(
                      '{proc}',
                      String(Math.round((1 - wezel.dysk.wolneBajty / wezel.dysk.caleBajty) * 100)),
                    )}
                    <span className="ml-2 text-muted-foreground">
                      {t('kopie.wolne')
                        .replace('{wolne}', bajty(wezel.dysk.wolneBajty))
                        .replace('{calkowite}', bajty(wezel.dysk.caleBajty))}
                    </span>
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={
                      wezel.dysk.wolneBajty / wezel.dysk.caleBajty < 0.1
                        ? 'h-full rounded-full bg-destructive'
                        : 'h-full rounded-full bg-primary'
                    }
                    style={{
                      width: `${Math.max(2, Math.round((1 - wezel.dysk.wolneBajty / wezel.dysk.caleBajty) * 100))}%`,
                    }}
                  />
                </div>
                {/**
                  * ⚠️ OSTRZEŻENIE PRZY 10 %, bo to próg, przy którym agent ODMÓWI kolejnej kopii —
                  * i lepiej, żeby człowiek dowiedział się z ekranu niż z nieudanej operacji.
                  */}
                {wezel.dysk.wolneBajty / wezel.dysk.caleBajty < 0.1 && (
                  <p className="text-xs text-destructive">{t('kopie.maloMiejsca')}</p>
                )}
                {wezel.dysk.kiedy !== null && (
                  <p className="text-[11px] text-muted-foreground">
                    {t('kopie.zmierzono').replace('{kiedy}', new Date(wezel.dysk.kiedy).toLocaleString('pl-PL'))}
                  </p>
                )}
              </div>
            ) : (
              <Pole label={t('mcard.f.wolne')}>
                <span className="text-muted-foreground">{t('kopie.dyskNieznany')}</span>
              </Pole>
            )}
            {/**
             * ⚠️ ROZJAZD, USŁUGA CZASU I PRÓG WYSPY STOJĄ RAZEM, bo osobno kłamią. „−29 s" jest
             * przejściowe na maszynie, która synchronizuje się sama, i TRWAŁE na tej, która ma
             * W32Time wyłączony — a próg mówi, czy agent w ogóle zareaguje.
             */}
            <Pole label={t('mcard.f.rozjazd')}>
              {wezel.zegar.rozjazdSekund === null ? (
                '—'
              ) : (
                <span
                  className={
                    Math.abs(wezel.zegar.rozjazdSekund) > wyspa.zegarProgSekund ? 'text-[var(--warning)]' : undefined
                  }
                >
                  {wezel.zegar.rozjazdSekund > 0 ? '+' : ''}
                  {wezel.zegar.rozjazdSekund} s
                </span>
              )}
            </Pole>
            <Pole label={t('mcard.f.w32time')}>
              <UslugaCzasu start={wezel.zegar.uslugaStart} dziala={wezel.zegar.uslugaDziala} />
            </Pole>
            <Pole label={t('mcard.f.korekta')}>
              {wyspa.zegarNaprawiaj ? (
                <span>
                  {t('mcard.wlaczona')}{' '}
                  <span className="text-xs text-muted-foreground">
                    {t('mcard.korektaOd').replace('{n}', String(wyspa.zegarProgSekund))}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {t('mcard.svc.disabled')}{' '}
                  <span className="text-xs">
                    {t('mcard.korektaWyl').replace('{n}', String(wyspa.zegarProgSekund))}
                  </span>
                </span>
              )}
            </Pole>
            <Pole label={t('mcard.f.strefa')}>
              {wezel.zegar.strefa ?? '—'}
              {wezel.zegar.strefa !== null && wyspa.timeZone !== null && wezel.zegar.strefa !== wyspa.timeZone && (
                <Badge variant="warning" className="ml-2">
                  {t('mcard.wyspaOczekuje').replace('{strefa}', wyspa.timeZone)}
                </Badge>
              )}
            </Pole>
            <Pole label={t('mcard.f.czasMaszyny')}>
              <span className="font-mono text-xs">{wezel.zegar.czasMaszyny ?? '—'}</span>
            </Pole>
          </Sekcja>
        )}

        {synchronizacja !== null && (
          <Sekcja tytul={t('mcard.s.sync')} ikona={RefreshCw}>
            {/**
             * ⚠️ TA SEKCJA POWSTAŁA Z AWARII, KTÓRA NIE MIAŁA OBJAWU (26.08.2026): kanał wydań
             * rodzeństwa nie wpisał ani jednego wiersza przez pół dnia, a portal pokazywał węzły
             * zdrowe. „Kiedy ostatnio pytał" jest tu najważniejszym wierszem — tylko on rozdziela
             * „pyta i nie ma czego wpisywać" od „nie pyta wcale".
             */}
            <Pole label={t('mcard.f.rodzenstwo')}>
              {synchronizacja.rodzenstwoWlaczone ? (
                <Badge variant="success">{t('mcard.wlaczone')}</Badge>
              ) : (
                <span className="text-muted-foreground">{t('mcard.wylaczoneNaWyspie')}</span>
              )}
            </Pole>
            {/**
              * ⚠️ NIE POKAZUJEMY TEGO JAKO UŁAMKA „X / Y", i to jest poprawka po obejrzeniu karty
              * na wdrożeniu (26.08.2026). `seq` jest numerem GLOBALNYM w wyspie, nie licznikiem
              * per automat, więc najwyższe OBCE wydanie bywa NIŻSZE od kursora — u małpkomata
              * wyszło „14839 / 7790", co czyta się jak zepsuty postęp, a znaczy „nadąża".
              * Wniosek: kursor to znacznik miejsca, nie licznik, i tak ma być pokazany.
              */}
            <Pole label={t('mcard.f.kursor')}>
              <span className="font-mono tabular-nums">{synchronizacja.kursor ?? t('mcard.nieustawiony')}</span>
              {synchronizacja.kursor !== null &&
                (synchronizacja.szczytObcych > synchronizacja.kursor ? (
                  <Badge variant="warning" className="ml-2">
                    {t('mcard.czekaDociagniecie').replace('{n}', String(synchronizacja.szczytObcych))}
                  </Badge>
                ) : (
                  <span className="ml-2 text-xs text-muted-foreground">{t('mcard.nadaza')}</span>
                ))}
            </Pole>
            <Pole label={t('mcard.f.wpisanych')}>
              <span className="tabular-nums">{synchronizacja.wpisanych}</span>
            </Pole>
            <Pole label={t('mcard.f.pytal')}>
              {synchronizacja.pytalO === null ? (
                <span className="text-[var(--warning)]">{t('mcard.nigdy')}</span>
              ) : (
                czas(synchronizacja.pytalO)
              )}
            </Pole>
            <Pole label={t('mcard.f.meldunek')}>{czas(synchronizacja.meldunekO)}</Pole>
            {synchronizacja.blad !== null && (
              <Pole label={t('mcard.f.bladWpisu')}>
                <span className="text-destructive">{synchronizacja.blad}</span>
              </Pole>
            )}
          </Sekcja>
        )}

        <Sekcja tytul={t('mcard.s.towary')} ikona={Package} licznik={`${wydania.najczestsze.length}`}>
          {/**
           * ⚠️ WYŁĄCZNIE `WN`. Zwroty (`REN`), korekty i uzupełnienia wrzucone do tej samej sumy
           * zamieniłyby pytanie „co ludzie stąd biorą" w „co się na tym automacie działo".
           */}
          {wydania.najczestsze.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('mcard.brakWydan')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="pb-1 font-normal">{t('mcard.th.towar')}</th>
                  <th className="pb-1 text-right font-normal">{t('mcard.th.sztuk')}</th>
                  <th className="pb-1 text-right font-normal">{t('mcard.th.wydan')}</th>
                </tr>
              </thead>
              <tbody>
                {wydania.najczestsze.map((x) => (
                  <tr key={x.itemNumber ?? String(x.sztuk)} className="border-t border-border/60">
                    <td className="py-1.5">
                      <span className="block truncate">{x.name ?? x.itemNumber ?? '—'}</span>
                      {x.name !== null && x.itemNumber !== null && x.name !== x.itemNumber && (
                        <span className="font-mono text-[10px] text-muted-foreground">{x.itemNumber}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{x.sztuk}</td>
                    <td className="py-1.5 text-right tabular-nums text-muted-foreground">{x.wydan}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Sekcja>

        {ruch !== null && (
          <Sekcja tytul={t('mcard.s.ruch')} ikona={Clock} licznik={`${ruch.dni} dni`}>
            {/**
             * ⚠️ PER WĘZEŁ, NIE PER AUTOMAT — ruch mierzymy na kanale agenta. Na maszynie bez
             * agenta ta sekcja się nie pokazuje, zamiast pokazywać zero jak fakt.
             */}
            <Pole label={t('mcard.f.razem')}>
              {bajty(ruch.razemDoPortalu + ruch.razemZPortalu)}
              <span className="ml-1 text-xs text-muted-foreground">
                ({bajty(ruch.razemDoPortalu)} {t('mcard.wGore')}, {bajty(ruch.razemZPortalu)} {t('mcard.wDol')})
              </span>
            </Pole>
            <Pole label={t('mcard.f.zadan')}>
              <span className="tabular-nums">{ruch.razemZadan.toLocaleString('pl-PL')}</span>
            </Pole>
            {ruch.rodzaje.map((r) => (
              /**
               * ⚠️ ETYKIETY Z `traffic.kind.*`, TE SAME CO NA EKRANIE ZUŻYCIA DANYCH. Surowa nazwa
               * kubełka („rodzenstwo") jest nazwą techniczną i dwa ekrany nazywające to samo
               * inaczej to dwa ekrany, które trzeba czytać osobno.
               */
              <Pole key={r.kind} label={t(`traffic.kind.${r.kind}`)}>
                {bajty(r.doPortalu + r.zPortalu)}
                <span className="ml-1 text-xs text-muted-foreground">
                  {r.zadan.toLocaleString('pl-PL')} {t('mcard.f.zadan')}
                </span>
              </Pole>
            ))}
          </Sekcja>
        )}
      </div>

      <Sekcja tytul={t('mcard.s.setup')} ikona={Settings2} licznik={`${ustawieniaSps.length}`}>
        {/**
         * ⚠️ TYLKO ODCZYT, ŚWIADOMIE. Zapis tych parametrów to config-down do tabeli `SetupParams`
         * na maszynie klienta — osobna decyzja i osobne zamki. Pokazanie ich już teraz ma wartość:
         * do tej pory nie były widoczne w portalu NIGDZIE.
         *
         * ⚠️ `value` I `valueCustom` W OSOBNYCH KOLUMNACH: pierwsze to wartość parametru, drugie
         * nadpisanie zrobione u klienta. Zlepienie ukryłoby fakt, że ktoś ten parametr ruszył.
         */}
        {ustawieniaSps.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('mcard.brakParametrow')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="pb-1 font-normal">{t('mcard.th.param')}</th>
                  <th className="pb-1 font-normal">{t('mcard.th.wartosc')}</th>
                  <th className="pb-1 font-normal">{t('mcard.th.nadpisanie')}</th>
                  <th className="pb-1 font-normal">{t('mcard.th.opis')}</th>
                </tr>
              </thead>
              <tbody>
                {ustawieniaSps.map((p) => (
                  <tr key={p.param} className="border-t border-border/60">
                    <td className="py-1.5 font-mono text-xs">{p.param}</td>
                    <td className="py-1.5 font-mono text-xs">{p.value ?? '—'}</td>
                    <td className="py-1.5 font-mono text-xs">
                      {p.valueCustom === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="text-[var(--warning)]">{p.valueCustom}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs text-muted-foreground">{p.descr ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sekcja>
      </>
      )}

      {spsConfigOtwarty && (
        <SpsConfigDialog
          maszyna={{ id: automat.id, name: automat.name }}
          groupId={groupId}
          onClose={() => setSpsConfigOtwarty(false)}
        />
      )}
    </div>
  );
}
